import { differenceInHours, differenceInMinutes, parseISO } from "date-fns";

interface CalculateScheduledProvisionTimeParams {
  nextShiftStart: string;
  autoProvisionBeforeShiftThresholdInHours: number;
}

interface ScheduledProvisionTimeResult {
  scheduledTime: Date;
  displayText: string;
  isAlreadyScheduled: boolean;
}

/**
 * Calculates when a PCC account is scheduled to be provisioned based on
 * the next shift start time and the auto-provision threshold.
 *
 * @param params.nextShiftStart - ISO string of the next shift's start time
 * @param params.autoProvisionBeforeShiftThresholdInHours - Hours before shift when provisioning occurs
 * @returns Object containing scheduled time, display text, and whether it's already scheduled
 */
export function calculateScheduledProvisionTime(
  params: CalculateScheduledProvisionTimeParams
): ScheduledProvisionTimeResult {
  const { nextShiftStart, autoProvisionBeforeShiftThresholdInHours } = params;

  const shiftStartDate = parseISO(nextShiftStart);
  const scheduledTime = new Date(
    shiftStartDate.getTime() - autoProvisionBeforeShiftThresholdInHours * 60 * 60 * 1000
  );

  const now = new Date();
  const isAlreadyScheduled = scheduledTime <= now;

  if (isAlreadyScheduled) {
    return {
      scheduledTime,
      displayText: "Creates soon",
      isAlreadyScheduled: true,
    };
  }

  const hoursUntilProvisioning = differenceInHours(scheduledTime, now);
  const minutesUntilProvisioning = differenceInMinutes(scheduledTime, now);

  let displayText: string;
  if (hoursUntilProvisioning >= 1) {
    displayText = `Creates in ${hoursUntilProvisioning}h`;
  } else if (minutesUntilProvisioning >= 1) {
    displayText = `Creates in ${minutesUntilProvisioning}m`;
  } else {
    displayText = "Creates soon";
  }

  return {
    scheduledTime,
    displayText,
    isAlreadyScheduled,
  };
}
